import { useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { isFunction, compact } from "lodash"
import { AppBar, Toolbar, Typography, Drawer, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { hasPermission, PERMISSION_LEVELS } from "helpers/permissions"
import { getCurrentTemplateMetrics } from "connected-components/asset-view/templates/custom/helpers"
import MetricsSelectorFooter from "./metrics-selector-footer.jsx"
import MetricsSelectorHeader from "./metrics-selector-header"
import { AvailableMetricsShape } from "./helpers/prop-types"
import MetricSelection from "./metric-selection"
import MetricColumnOrdering from "./metric-column-ordering"
import { filterProcessedMetrics, processMetrics } from "./helpers"
import { MetricsTemplateShape } from "./helpers/prop-types"
import "./styles.scss"

export default function MetricSelector({
  isOpen,
  toggle,
  closeWithoutRestore,
  defaultSelectedMetrics,
  availableMetrics,
  includeMetricTypes = false,
  saveCustomTemplate,
  deleteCustomTemplate,
  customTemplate,
  analytics,
  modelId,
  initialViewName,
}) {
  const [metricsSearchValue, setMetricsSearchValue] = useState("")

  const [localCustomViewName, setLocalCustomViewName] = useState(() =>
    customTemplate?.name || initialViewName || "",
  )

  const [canSaveView, setCanSaveView] = useState(() =>
    !!customTemplate || !!initialViewName,
  )

  const [localSelectedMetrics, setLocalSelectedMetrics] = useState(() => {
    if (customTemplate?.config?.metrics) {
      const metrics = customTemplate.config.metrics
      return getCurrentTemplateMetrics(metrics, modelId)
    }
    return defaultSelectedMetrics
  })

  const hasDeleteAccess = useMemo(
    () => hasPermission(customTemplate?.permissionLevel, PERMISSION_LEVELS.OWNER),
    [customTemplate],
  )

  const { processedAvailableMetrics, availableMetricsMap } = useMemo(() =>
    processMetrics(availableMetrics, localSelectedMetrics),
  [availableMetrics, localSelectedMetrics])

  const selectedMetricsMap = useMemo(() => {
    if (!customTemplate || !customTemplate.config.metrics) {
      return {}
    }
    const metrics = customTemplate.config.metrics
    const validMetrics = metrics.filter(metric => {
      return typeof metric === "string" && metric.length > 0
    })

    const selectedMetrics = getCurrentTemplateMetrics(validMetrics, modelId)

    return selectedMetrics.reduce((acc, metric, index) => {
      acc[metric] = validMetrics[index]
      return acc
    }, {})
  }, [customTemplate, modelId])

  const metricsMap = useMemo(() => ({ ...selectedMetricsMap, ...availableMetricsMap }), [availableMetricsMap, selectedMetricsMap])

  const saveSelectedMetrics = useCallback(() => {
    const closeSelector = closeWithoutRestore ?? toggle
    closeSelector()
    const metrics = localSelectedMetrics.map((metricKey) => metricsMap[metricKey])
    void saveCustomTemplate({ name: localCustomViewName, metrics: compact(metrics) })
  }, [saveCustomTemplate, localCustomViewName, localSelectedMetrics, closeWithoutRestore, toggle, metricsMap])

  const deleteSelectedMetrics = useCallback(() => {
    deleteCustomTemplate(customTemplate)
    const closeSelector = closeWithoutRestore ?? toggle
    closeSelector()
  }, [deleteCustomTemplate, customTemplate, closeWithoutRestore, toggle])

  const filteredMetrics = useMemo(() =>
    filterProcessedMetrics(processedAvailableMetrics, metricsSearchValue),
  [processedAvailableMetrics, metricsSearchValue])

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={toggle}
      classes={{
        paper: "components_custom-metrics-selector",
      }}
    >
      <AppBar
        position="sticky"
        classes={{
          root: "metrics-selector-app-bar",
        }}
        sx={{
          boxShadow: "none",
        }}
      >
        <Toolbar className="metrics-selector-toolbar">
          <Typography
            variant="h6"
            noWrap
            sx={{
            color: "inherit",
          }}
          >
            Create a Custom View
          </Typography>
          {isFunction(toggle) && <IconButton onClick={toggle} color="inherit">
            <CloseIcon />
          </IconButton>}
        </Toolbar>
      </AppBar>
      <div className="custom-metrics-selector-wrapper">
        <MetricsSelectorHeader
          customViewName={localCustomViewName}
          setCustomViewName={setLocalCustomViewName}
          setCanSaveView={setCanSaveView}
        />
        <div className="metrics-selector-content">
          <div className="left-side">
            <MetricSelection
              shouldFocus={isOpen}
              filteredMetrics={filteredMetrics}
              selectedMetrics={localSelectedMetrics}
              setSelectedMetrics={setLocalSelectedMetrics}
              metricsSearchValue={metricsSearchValue}
              setMetricsSearchValue={setMetricsSearchValue}
              includeMetricTypes={includeMetricTypes}
              analytics={analytics}
            />
          </div>
          <div className="right-side">
            <MetricColumnOrdering
              modelId={modelId}
              availableMetrics={processedAvailableMetrics}
              selectedMetrics={localSelectedMetrics}
              metricsMap={metricsMap}
              setSelectedMetrics={setLocalSelectedMetrics}
              analytics={analytics}
            />
          </div>
        </div>
      </div>
      <MetricsSelectorFooter
        deleteSelectedMetrics={deleteSelectedMetrics}
        saveSelectedMetrics={saveSelectedMetrics}
        canSaveView={canSaveView && localSelectedMetrics.length > 0}
        isEditing={!!customTemplate}
        hasDeleteAccess={hasDeleteAccess}
        analytics={analytics}
      />
    </Drawer>
  )
}

MetricSelector.propTypes = {
  modelId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  closeWithoutRestore: PropTypes.func,
  availableMetrics: AvailableMetricsShape,
  saveCustomTemplate: PropTypes.func.isRequired,
  deleteCustomTemplate: PropTypes.func.isRequired,
  defaultSelectedMetrics: PropTypes.arrayOf(PropTypes.string).isRequired,
  includeMetricTypes: PropTypes.bool,
  customTemplate: MetricsTemplateShape,
  analytics: PropTypes.object,
  initialViewName: PropTypes.string,
}
